import { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    MessageSquareQuote, Plus, Save, Trash2, Eye, EyeOff, Star, ChevronUp, ChevronDown,
    Sparkles, Loader2, AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import {
    fetchAdminReviews, createReview, updateReview, deleteReview, reorderReviews,
} from "@/lib/adminApi";

const SOURCE_OPTIONS = [
    { value: "google",    label: "Google" },
    { value: "instagram", label: "Instagram" },
    { value: "behance",   label: "Behance" },
    { value: "facebook",  label: "Facebook" },
    { value: "linkedin",  label: "LinkedIn" },
    { value: "upwork",    label: "Upwork" },
    { value: "fiverr",    label: "Fiverr" },
    { value: "email",     label: "Email" },
    { value: "direct",    label: "Direct / Client" },
    { value: "twitter",   label: "X / Twitter" },
    { value: "youtube",   label: "YouTube" },
];

const emptyReview = () => ({
    id: null,
    name: "",
    role: "",
    photo: "",
    source: "google",
    rating: 5,
    quote: "",
    project: "",
    location: "",
    featured: false,
    visible: true,
    order: 0,
});

export default function AdminReviews() {
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [draft, setDraft] = useState(null);
    const [editing, setEditing] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await fetchAdminReviews();
            setReviews(Array.isArray(data.reviews) ? data.reviews : []);
        } catch (_e) {
            toast.error("Could not load reviews");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const sorted = useMemo(
        () => [...reviews].sort((a, b) => (a.featured === b.featured ? (a.order || 0) - (b.order || 0) : a.featured ? -1 : 1)),
        [reviews]
    );

    const openNew = () => {
        setDraft({ ...emptyReview(), order: sorted.length });
        setEditing(null);
    };
    const openEdit = (r) => {
        setDraft({ ...emptyReview(), ...r });
        setEditing(r.id);
    };
    const closeForm = () => { setDraft(null); setEditing(null); };

    const onSubmit = async (data) => {
        if (!data.name?.trim() || !data.quote?.trim()) {
            toast.error("Name and quote are required");
            return;
        }
        try {
            if (editing) {
                const updated = await updateReview(editing, data);
                setReviews((arr) => arr.map((x) => (x.id === editing ? updated : x)));
                toast.success("Review updated");
            } else {
                const created = await createReview(data);
                setReviews((arr) => [...arr, created]);
                toast.success("Review added");
            }
            closeForm();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error("Could not save", { description: typeof detail === "string" ? detail : "Please retry." });
        }
    };

    const onDelete = async (id) => {
        if (!window.confirm("Delete this review? This cannot be undone.")) return;
        try {
            await deleteReview(id);
            setReviews((arr) => arr.filter((x) => x.id !== id));
            toast.success("Deleted");
            if (editing === id) closeForm();
        } catch (_e) {
            toast.error("Could not delete");
        }
    };

    const onToggleVisible = async (r) => {
        try {
            const updated = await updateReview(r.id, { visible: !r.visible });
            setReviews((arr) => arr.map((x) => (x.id === r.id ? updated : x)));
        } catch (_e) { toast.error("Could not update"); }
    };

    const onToggleFeatured = async (r) => {
        try {
            const updated = await updateReview(r.id, { featured: !r.featured });
            setReviews((arr) => arr.map((x) => (x.id === r.id ? updated : x)));
        } catch (_e) { toast.error("Could not update"); }
    };

    const moveItem = async (idx, dir) => {
        const next = [...sorted];
        const target = idx + dir;
        if (target < 0 || target >= next.length) return;
        [next[idx], next[target]] = [next[target], next[idx]];
        const ids = next.map((r) => r.id);
        const newOrders = next.map((r, i) => ({ ...r, order: i }));
        setReviews(newOrders);
        try { await reorderReviews(ids); }
        catch (_e) { toast.error("Could not reorder"); load(); }
    };

    return (
        <div className="px-5 py-8 sm:px-8 sm:py-10 lg:px-10">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <span className="pg-eyebrow inline-flex items-center gap-2">
                        <Sparkles className="h-3.5 w-3.5" /> Studio CMS · Reviews
                    </span>
                    <h1
                        className="font-display mt-4 text-3xl sm:text-4xl"
                        style={{ color: "var(--pg-fg)" }}
                        data-testid="admin-reviews-headline"
                    >
                        Client Reviews
                    </h1>
                    <p className="mt-2 max-w-2xl text-sm" style={{ color: "var(--pg-fg-muted)" }}>
                        Add testimonials from Google, Instagram, Behance, Email, etc. They appear in the homepage Reviews
                        section. Featured reviews are pinned first, then ordered manually.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={openNew}
                    className="pg-btn-primary pg-app-tap"
                    data-testid="admin-reviews-add"
                >
                    <Plus className="h-3.5 w-3.5" /> Add Review
                </button>
            </div>

            <AnimatePresence>
                {draft && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="mt-6"
                    >
                        <ReviewEditor draft={draft} isNew={!editing} onSubmit={onSubmit} onCancel={closeForm} />
                    </motion.div>
                )}
            </AnimatePresence>

            <div className="mt-6">
                {loading ? (
                    <div
                        className="flex items-center justify-center rounded-2xl border px-6 py-20 text-sm"
                        style={{ borderColor: "var(--pg-border)", color: "var(--pg-fg-muted)" }}
                    >
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading reviews…
                    </div>
                ) : sorted.length === 0 ? (
                    <div
                        className="rounded-2xl border px-6 py-14 text-center"
                        style={{ borderColor: "var(--pg-border)", color: "var(--pg-fg-muted)" }}
                        data-testid="admin-reviews-empty"
                    >
                        <AlertCircle className="mx-auto mb-2 h-5 w-5" />
                        <p className="text-sm">No reviews yet. Click <strong>Add Review</strong> to create your first one.</p>
                        <p className="mt-1 text-xs">Until you add at least one, the public Reviews section will stay hidden.</p>
                    </div>
                ) : (
                    <ul className="grid grid-cols-1 gap-4 lg:grid-cols-2" data-testid="admin-reviews-list">
                        {sorted.map((r, idx) => (
                            <ReviewRow
                                key={r.id}
                                review={r}
                                idx={idx}
                                total={sorted.length}
                                onEdit={() => openEdit(r)}
                                onDelete={() => onDelete(r.id)}
                                onToggleVisible={() => onToggleVisible(r)}
                                onToggleFeatured={() => onToggleFeatured(r)}
                                onMoveUp={() => moveItem(idx, -1)}
                                onMoveDown={() => moveItem(idx, 1)}
                            />
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}

const ReviewEditor = ({ draft, isNew, onSubmit, onCancel }) => {
    const [data, setData] = useState(draft);
    useEffect(() => { setData(draft); }, [draft]);
    const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

    const submit = (e) => { e.preventDefault(); onSubmit(data); };

    return (
        <form onSubmit={submit} className="pg-app-surface p-5 sm:p-7" data-testid="admin-reviews-editor">
            <h2 className="font-display text-xl sm:text-2xl" style={{ color: "var(--pg-fg)" }}>
                {isNew ? "New Review" : "Edit Review"}
            </h2>

            <div className="mt-5 grid gap-4 sm:grid-cols-2">
                <Field label="Client Name *" testId="admin-reviews-name">
                    <input type="text" required value={data.name} onChange={(e) => set("name", e.target.value)} className="pg-input" placeholder="Sarah Mitchell" />
                </Field>
                <Field label="Role / Title" testId="admin-reviews-role">
                    <input type="text" value={data.role} onChange={(e) => set("role", e.target.value)} className="pg-input" placeholder="Founder, BEG Imports" />
                </Field>
            </div>

            <Field label="Quote / Review Text *" testId="admin-reviews-quote" className="mt-4">
                <textarea
                    rows={4}
                    required
                    value={data.quote}
                    onChange={(e) => set("quote", e.target.value)}
                    className="pg-input"
                    style={{ resize: "vertical", minHeight: 100 }}
                    placeholder="Pranto re-imagined our brand identity from scratch and the result has been transformative..."
                />
            </Field>

            <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <Field label="Source" testId="admin-reviews-source">
                    <select value={data.source} onChange={(e) => set("source", e.target.value)} className="pg-input">
                        {SOURCE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                </Field>
                <Field label="Star Rating" testId="admin-reviews-rating">
                    <div className="flex items-center gap-2">
                        {[1,2,3,4,5].map((n) => (
                            <button
                                key={n}
                                type="button"
                                onClick={() => set("rating", data.rating === n ? 0 : n)}
                                className="pg-app-tap inline-flex h-9 w-9 items-center justify-center rounded-md border"
                                style={{
                                    borderColor: "var(--pg-border)",
                                    background: n <= (data.rating || 0) ? "color-mix(in srgb, var(--pg-accent) 14%, var(--pg-surface))" : "var(--pg-surface)",
                                }}
                                data-testid={`admin-reviews-star-${n}`}
                                aria-label={`${n} star${n > 1 ? "s" : ""}`}
                            >
                                <Star
                                    className="h-3.5 w-3.5"
                                    style={{
                                        color: n <= (data.rating || 0) ? "var(--pg-accent)" : "var(--pg-fg-muted)",
                                        fill: n <= (data.rating || 0) ? "var(--pg-accent)" : "transparent",
                                    }}
                                    strokeWidth={1.4}
                                />
                            </button>
                        ))}
                        <span className="text-[11px]" style={{ color: "var(--pg-fg-muted)" }}>
                            {data.rating || 0} / 5 · click again to clear
                        </span>
                    </div>
                </Field>
            </div>

            <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <Field label="Project (optional)" testId="admin-reviews-project">
                    <input type="text" value={data.project} onChange={(e) => set("project", e.target.value)} className="pg-input" placeholder="BEG Imports Brand Identity" />
                </Field>
                <Field label="Location (optional)" testId="admin-reviews-location">
                    <input type="text" value={data.location} onChange={(e) => set("location", e.target.value)} className="pg-input" placeholder="Dubai, UAE" />
                </Field>
            </div>

            <Field label="Client Photo URL (optional)" testId="admin-reviews-photo" className="mt-4">
                <input type="url" value={data.photo} onChange={(e) => set("photo", e.target.value)} className="pg-input" placeholder="https://… (falls back to elegant initials if blank)" />
            </Field>

            <div className="mt-5 flex flex-wrap items-center gap-4">
                <label className="inline-flex items-center gap-2 text-sm" style={{ color: "var(--pg-fg)" }}>
                    <input type="checkbox" checked={!!data.featured} onChange={(e) => set("featured", e.target.checked)} data-testid="admin-reviews-featured" />
                    Featured (pinned first)
                </label>
                <label className="inline-flex items-center gap-2 text-sm" style={{ color: "var(--pg-fg)" }}>
                    <input type="checkbox" checked={data.visible !== false} onChange={(e) => set("visible", e.target.checked)} data-testid="admin-reviews-visible" />
                    Visible on public site
                </label>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-2">
                <button type="submit" className="pg-btn-primary pg-app-tap" data-testid="admin-reviews-save">
                    <Save className="h-4 w-4" /> {isNew ? "Add Review" : "Save Changes"}
                </button>
                <button type="button" onClick={onCancel} className="pg-btn-ghost pg-app-tap" data-testid="admin-reviews-cancel">
                    Cancel
                </button>
            </div>
        </form>
    );
};

const ReviewRow = ({ review, idx, total, onEdit, onDelete, onToggleVisible, onToggleFeatured, onMoveUp, onMoveDown }) => {
    const hidden = review.visible === false;
    return (
        <li
            className="rounded-2xl border p-4 sm:p-5"
            style={{
                borderColor: "var(--pg-border)",
                background: "var(--pg-surface)",
                opacity: hidden ? 0.55 : 1,
            }}
            data-testid="admin-reviews-row"
            data-review-id={review.id}
        >
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="font-display text-base sm:text-lg" style={{ color: "var(--pg-fg)" }}>
                            {review.name || "Untitled"}
                        </span>
                        {review.featured && (
                            <span
                                className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-mono uppercase tracking-[0.16em]"
                                style={{ borderColor: "var(--pg-border-strong)", background: "color-mix(in srgb, var(--pg-accent) 16%, transparent)", color: "var(--pg-fg)" }}
                            >
                                <Star className="h-2.5 w-2.5" style={{ color: "var(--pg-accent)", fill: "var(--pg-accent)" }} /> Featured
                            </span>
                        )}
                        {hidden && (
                            <span
                                className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-mono uppercase tracking-[0.16em]"
                                style={{ borderColor: "var(--pg-border-strong)", background: "var(--pg-surface-2)", color: "var(--pg-fg-muted)" }}
                            >
                                <EyeOff className="h-2.5 w-2.5" /> Hidden
                            </span>
                        )}
                    </div>
                    <p className="mt-0.5 text-[12px] font-mono uppercase tracking-[0.18em]" style={{ color: "var(--pg-fg-muted)" }}>
                        {review.role || "—"}{review.source ? ` · ${review.source}` : ""}{review.rating ? ` · ${review.rating}★` : ""}
                    </p>
                    <p className="mt-2 line-clamp-3 text-sm leading-relaxed" style={{ color: "var(--pg-fg)" }}>
                        “{review.quote}”
                    </p>
                </div>

                <div className="flex flex-col gap-1">
                    <button type="button" onClick={onMoveUp} disabled={idx === 0} aria-label="Move up"
                        className="inline-flex h-7 w-7 items-center justify-center rounded border transition-colors hover:bg-[var(--pg-surface-2)] disabled:opacity-40"
                        style={{ borderColor: "var(--pg-border)", color: "var(--pg-fg-muted)" }}>
                        <ChevronUp className="h-3 w-3" />
                    </button>
                    <button type="button" onClick={onMoveDown} disabled={idx === total - 1} aria-label="Move down"
                        className="inline-flex h-7 w-7 items-center justify-center rounded border transition-colors hover:bg-[var(--pg-surface-2)] disabled:opacity-40"
                        style={{ borderColor: "var(--pg-border)", color: "var(--pg-fg-muted)" }}>
                        <ChevronDown className="h-3 w-3" />
                    </button>
                </div>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-2">
                <button type="button" onClick={onEdit} className="pg-btn-ghost pg-app-tap" data-testid={`admin-reviews-edit-${review.id}`}>Edit</button>
                <button type="button" onClick={onToggleVisible} className="pg-btn-ghost pg-app-tap" data-testid={`admin-reviews-visible-${review.id}`}>
                    {hidden ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                    {hidden ? "Show" : "Hide"}
                </button>
                <button type="button" onClick={onToggleFeatured} className="pg-btn-ghost pg-app-tap" data-testid={`admin-reviews-feature-${review.id}`}>
                    <Star className="h-3.5 w-3.5" style={{ color: review.featured ? "var(--pg-accent)" : "inherit", fill: review.featured ? "var(--pg-accent)" : "transparent" }} />
                    {review.featured ? "Unfeature" : "Feature"}
                </button>
                <button
                    type="button"
                    onClick={onDelete}
                    className="ml-auto inline-flex h-8 items-center gap-1.5 rounded-md border px-3 text-xs transition-colors hover:bg-[var(--pg-surface-2)]"
                    style={{ borderColor: "var(--pg-border)", color: "#E55353" }}
                    data-testid={`admin-reviews-delete-${review.id}`}
                >
                    <Trash2 className="h-3.5 w-3.5" /> Delete
                </button>
            </div>
        </li>
    );
};

const Field = ({ label, children, testId, className }) => (
    <div className={`flex flex-col gap-1.5 ${className || ""}`} data-testid={testId}>
        <span className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: "var(--pg-fg-muted)" }}>{label}</span>
        {children}
    </div>
);
